import React, { useContext, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography
} from "@mui/material"
import { amber, green, grey, red } from "@mui/material/colors"
import { ExpandMore } from "@mui/icons-material"
import { LoadingButton } from "@mui/lab"
import Editor from "@monaco-editor/react"
import {
    useErasePersonalDataMutation,
    useGetThanosProjectsQuery,
    useSearchPersonalDataMutation
} from "../../core/api/personal-data-api"
import { formatJSON } from "../../core/utils/constants"
import { SnackBarContext } from "../../snackBarProvider"

const statusBg = (status, hasChildren) => {
    if (status === "error") return red[50]
    if (!hasChildren) return amber[50]
    if (status === "ok") return green[50]
    return grey[50]
}

const JsonBlock = ({ value }) => {
    const text = useMemo(() => formatJSON(value, "null", 2), [value])
    const lines = text.split("\n").length
    const height = Math.min(Math.max(lines * 18 + 20, 80), 480)

    return (
        <Box sx={{ border: 1, borderColor: "divider", overflow: "hidden" }}>
            <Editor
                height={height}
                defaultLanguage="json"
                value={text}
                options={{
                    fontFamily: "JetBrains Mono Variable",
                    readOnly: true,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    lineNumbers: "off",
                    folding: true,
                    fontSize: 12
                }}
            />
        </Box>
    )
}

const CollectorAccordion = ({ collector }) => (
    <Accordion disableGutters
               sx={{ background: statusBg(collector.status, collector.ids && Object.keys(collector.ids).length > 0) }}>
        <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography sx={{ fontWeight: 500 }}>{collector.collector}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            {collector.error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {collector.error.code}: {collector.error.message}
                </Alert>
            )}
            {collector.ids && Object.keys(collector.ids).length > 0 ? (
                <Table size="small">
                    <TableBody>
                        {Object.entries(collector.ids).map(([key, value]) => (
                            <TableRow key={key}>
                                <TableCell sx={{ fontWeight: 500, width: 200 }}>{key}</TableCell>
                                <TableCell sx={{ fontFamily: "monospace" }}>{String(value)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : (
                <Typography color="text.secondary">No ids</Typography>
            )}
        </AccordionDetails>
    </Accordion>
)

const EntityAccordion = ({ entity }) => (
    <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography>{entity.entity}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            <JsonBlock value={entity.data}/>
        </AccordionDetails>
    </Accordion>
)

const ServiceAccordion = ({ service }) => (
    <Accordion disableGutters sx={{ background: statusBg(service.status, service.entities?.length > 0) }}>
        <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography sx={{ fontWeight: 500 }}>
                {service.service}
            </Typography>
        </AccordionSummary>
        <AccordionDetails>
            {service.error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {service.error.code}: {service.error.message}
                </Alert>
            )}
            {service.entities?.length > 0 ? (
                <Stack spacing={0.5}>
                    {service.entities.map((entity, idx) => (
                        <EntityAccordion key={`${entity.entity}-${idx}`} entity={entity}/>
                    ))}
                </Stack>
            ) : (
                !service.error && <Typography color="text.secondary">No entities</Typography>
            )}
        </AccordionDetails>
    </Accordion>
)

const ProjectAccordion = ({ project }) => (
    <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore/>}>
            <Typography variant="h6">{project.project}</Typography>
        </AccordionSummary>
        <AccordionDetails>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Collectors</Typography>
                    {project.collectors?.length > 0 ? (
                        <Stack spacing={0.5}>
                            {project.collectors.map((collector, idx) => (
                                <CollectorAccordion key={`${collector.collector}-${idx}`} collector={collector}/>
                            ))}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">No collectors</Typography>
                    )}
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Services</Typography>
                    {project.services?.length > 0 ? (
                        <Stack spacing={0.5}>
                            {project.services.map((service, idx) => (
                                <ServiceAccordion key={`${service.service}-${idx}`} service={service}/>
                            ))}
                        </Stack>
                    ) : (
                        <Typography color="text.secondary">No services</Typography>
                    )}
                </Box>
            </Stack>
        </AccordionDetails>
    </Accordion>
)

const getErrorMessage = (error) => {
    if (!error) return "Unknown error"
    if (typeof error.data === "string") return error.data
    if (error.data?.message) return error.data.message
    if (error.data?.error) return typeof error.data.error === "string" ? error.data.error : formatJSON(error.data.error)
    if (error.error) return error.error
    if (error.status) return `Request failed: ${error.status}`
    return formatJSON(error)
}

export default function PersonalData({ initialProject }) {
    const { addAlert } = useContext(SnackBarContext)
    const mode = useSelector((state) => state.data.SourceMode)

    const {
        data: projects = [],
        isLoading: isLoadingProjects,
        isError: isProjectsError,
        error: projectsError
    } = useGetThanosProjectsQuery(undefined, { skip: mode.isStaging })

    const [searchPersonalData, { isLoading: isSearching }] = useSearchPersonalDataMutation()
    const [erasePersonalData, { isLoading: isErasing }] = useErasePersonalDataMutation()

    const [project, setProject] = useState(initialProject?.trim() ?? "")
    const [userId, setUserId] = useState("")
    const [userEmail, setUserEmail] = useState("")
    const [result, setResult] = useState(null)
    const [searchError, setSearchError] = useState(null)

    const [eraseOpen, setEraseOpen] = useState(false)
    const [reason, setReason] = useState("")
    const [confirmText, setConfirmText] = useState("")

    const canSearch = project && userId.trim()
    const canErase = reason.trim() && confirmText === "CONFIRM"

    const handleSearch = async () => {
        setSearchError(null)
        setResult(null)
        try {
            const data = await searchPersonalData({
                project,
                userId: userId.trim(),
                userEmail: userEmail?.trim() ?? null
            }).unwrap()
            setResult(data)
        } catch (error) {
            setSearchError(error)
            addAlert({ text: getErrorMessage(error), type: "error" })
        }
    }

    const handleOpenErase = () => {
        setReason("")
        setConfirmText("")
        setEraseOpen(true)
    }

    const handleCloseErase = () => {
        if (isErasing) return
        setEraseOpen(false)
        setReason("")
        setConfirmText("")
    }

    const handleErase = async () => {
        try {
            await erasePersonalData({
                project,
                userId: userId.trim(),
                userEmail: userEmail?.trim() ?? null,
                reason: reason.trim()
            }).unwrap()
            addAlert({ text: "Personal data erase requested", type: "success" })
            setEraseOpen(false)
            setReason("")
            setConfirmText("")
            setResult(null)
        } catch (error) {
            addAlert({ text: getErrorMessage(error), type: "error" })
        }
    }

    if (mode.isStaging) {
        return <Alert severity="warning">GDPR available only in production mode</Alert>
    }

    if (isLoadingProjects) {
        return <CircularProgress/>
    }

    if (isProjectsError) {
        return <Alert severity="error">Failed to load projects: {getErrorMessage(projectsError)}</Alert>
    }

    return (
        <>
            <Typography variant="h5">Personal Data</Typography>

            <Card sx={{ p: 2 }}>
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={2}
                >
                    <TextField
                        required
                        fullWidth
                        size="small"
                        label="User ID"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        label="User Email"
                        value={userEmail}
                        onChange={(e) => setUserEmail(e.target.value)}
                        sx={{ minWidth: 240 }}
                    />
                    <LoadingButton
                        variant="contained"
                        disabled={!canSearch}
                        loading={isSearching}
                        onClick={handleSearch}
                    >
                        Search
                    </LoadingButton>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        disabled={!result}
                        onClick={handleOpenErase}
                    >
                        Erase
                    </LoadingButton>
                </Stack>
            </Card>

            {searchError && (
                <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(searchError)}</Alert>
            )}

            {result && (
                <Stack spacing={2}>
                    {result.projects?.length > 0 ? (
                        result.projects.map((p, idx) => (
                            <ProjectAccordion key={`${p.project}-${idx}`} project={p}/>
                        ))
                    ) : (
                        <Alert severity="info">No projects in response</Alert>
                    )}
                </Stack>
            )}

            <Dialog open={eraseOpen} onClose={handleCloseErase} maxWidth="sm" fullWidth>
                <DialogTitle>Erase personal data</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This action is irreversible. Type <strong>CONFIRM</strong> to proceed.
                        Project: <strong>{project}</strong>, User ID: <strong>{userId}</strong>
                    </DialogContentText>
                    <Stack spacing={2}>
                        <TextField
                            label="Reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            fullWidth
                            required
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Type CONFIRM"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            fullWidth
                            required
                            autoComplete="off"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseErase} disabled={isErasing}>Cancel</Button>
                    <LoadingButton
                        color="error"
                        variant="contained"
                        loading={isErasing}
                        disabled={!canErase}
                        onClick={handleErase}
                    >
                        Erase
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}
